import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import Search from 'Components/DesignSystem/Search/Search';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';

interface InfiniteScrollFilterListProps {
    title: string;
    selectedValues: string[];
    onToggle: (value: string) => void;
    fetchOptions: (page: number, search: string) => Promise<{ options: string[]; hasMore: boolean }>;
    showSearch?: boolean;
}

const InfiniteScrollFilterList: React.FC<InfiniteScrollFilterListProps> = ({
    title,
    selectedValues,
    onToggle,
    fetchOptions,
    showSearch = true
}) => {
    const [searchValue, setSearchValue] = useState('');
    // Display-only echo of what the user typed: the DS Search is a controlled
    // input, while `searchValue` stays the DEBOUNCED value that drives fetches
    // (exactly as before the 2026-09-05 DS migration).
    const [searchInput, setSearchInput] = useState('');
    const [page, setPage] = useState(1);
    const [options, setOptions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    /*
      § 288 — the SEARCH half of `isLoading`, kept apart from the scroll-page half.
      `isLoading` covers both, and a field that spun while the user scrolled the list
      would be claiming a search nobody asked for. `isNewSearch` is already the
      discriminator the loader takes; this only remembers it for the duration of the
      request. Presentational: nothing reads it but the field's `loading`.
    */
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observerTarget = useRef<HTMLDivElement>(null);

    // Load options
    const loadOptions = useCallback(async (pageNum: number, search: string, isNewSearch: boolean = false) => {
        if (isLoading) return;

        setIsLoading(true);
        if (isNewSearch) setIsSearchLoading(true);
        try {
            const result = await fetchOptions(pageNum, search);

            if (isNewSearch) {
                setOptions(result.options);
            } else {
                setOptions(prev => [...prev, ...result.options]);
            }

            setHasMore(result.hasMore);
        } catch (error) {
            console.error('Failed to load filter options:', error);
        } finally {
            setIsLoading(false);
            if (isNewSearch) setIsSearchLoading(false);
        }
    }, [fetchOptions, isLoading]);

    // Initial load and search changes
    useEffect(() => {
        setPage(1);
        setOptions([]);
        setHasMore(true);
        loadOptions(1, searchValue, true);
    }, [searchValue]);

    // Debounced search
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchValue(value);
        }, 500),
        []
    );

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    // Intersection Observer for infinite scroll
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoading) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadOptions(nextPage, searchValue, false);
                }
            },
            { threshold: 0.1, rootMargin: '50px' }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, isLoading, page, searchValue, loadOptions]);

    // Select All logic
    const isSelectAll = options.length > 0 && selectedValues.length === options.length;

    const handleSelectAll = () => {
        if (isSelectAll) {
            // Deselect all visible options
            options.forEach(option => {
                if (selectedValues.includes(option)) {
                    onToggle(option);
                }
            });
        } else {
            // Select all visible options
            options.forEach(option => {
                if (!selectedValues.includes(option)) {
                    onToggle(option);
                }
            });
        }
    };

    return (
        <div className="tw-h-full tw-flex tw-flex-col">
            {/* Search input — DS Search (2026-09-05); the 500ms debounce above is
                unchanged, only the control moved. */}
            {showSearch && (
                <div className="tw-pt-1 tw-pb-3">
                    <Search
                        value={searchInput}
                        onChange={handleSearchChange}
                        /* § 288 — the search-only flag, plus the 500ms debounce in front
                           of it (`searchInput` is what was typed, `searchValue` what the
                           fetch has been given). The scroll-page load has its own
                           affordance, the "Loading more options..." row below. */
                        loading={isSearchLoading || searchInput !== searchValue}
                        loadingLabel="Searching options"
                        placeholder="Search"
                        ariaLabel="Search"
                        searchIconPosition="end"
                        data-testid="components-infinitescrollfilterlist-input-1"
                    />
                </div>
            )}

            {/* Select All option for filters with multiple options. The testid kept
                its name but sits on the real <input> now (DS Checkbox), replacing the
                old label testid + duplicated inner-input testid pair (§ 7.1). */}
            {options.length > 2 && (
                <div className="tw-block">
                    <Checkbox
                        label="Select All"
                        checked={isSelectAll}
                        onChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                        className="tw-w-full tw-py-2"
                    />
                    <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
                </div>
            )}

            {/* Options list with infinite scroll */}
            <div className="tw-flex-1 tw-overflow-y-auto">
                {options.map((option) => {
                    const isSelected = selectedValues.includes(option);

                    return (
                        <div key={option} className="tw-block">
                            <Checkbox
                                label={option}
                                checked={isSelected}
                                onChange={() => onToggle(option)}
                                data-testid={`${title}-${option}-checkbox`}
                                className="tw-w-full tw-py-2 hover:tw-bg-surface-hover tw-transition-colors"
                            />
                            <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
                        </div>
                    );
                })}

                {/* Loading indicator */}
                {isLoading && (
                    <div className="tw-p-4 tw-text-center tw-text-sm tw-text-ink-tertiary">
                        Loading more options...
                    </div>
                )}

                {/* Sentinel element for intersection observer */}
                {hasMore && !isLoading && (
                    <div ref={observerTarget} className="tw-h-4" />
                )}

                {/* No more results indicator */}
                {!hasMore && options.length === 0 && (
                    <div className="tw-p-4 tw-text-center tw-text-sm tw-text-ink-tertiary">
                        No options found
                    </div>
                )}
            </div>
        </div>
    );
};

export default InfiniteScrollFilterList;